import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.sass';
import documentIcon from '../../../../assets/icons/document.png';
import emailIcon from '../../../../assets/icons/email.png';
import birthdateIcon from '../../../../assets/icons/birthdate.png';
import phoneIcon from '../../../../assets/icons/phone.png';
import locationIcon from '../../../../assets/icons/location.png';
import linkIcon from '../../../../assets/icons/link.png';
import companyIcon from '../../../../assets/icons/company_segment.png';
import editIcon from '../../../../assets/icons/edit.png';
import badgeIcon from '../../../../assets/icons/badge.png';
import maleIcon from '../../../../assets/icons/male-icon.svg';
import { Input } from '../../../components/input';
import ResponseModal from '../../../components/response-modal';
import { parseJwt } from '../../../../insfrastructure/utils/JWTParser';
import { getAuthToken } from '../../../../insfrastructure/services/token-manager';
import { AUTH_CONSTANTS } from '../../../../insfrastructure/utils/auth-constants';
import { convertSocialMediaToAPI } from '../../../../insfrastructure/services/company-profile';
import { formatCPF, formatCNPJ, formatPhone, formatDate, formatDateForAPI, removeFormatting } from './utils/formatters';
import { extractApiErrorMessage } from './utils/apiError';
import { useGenderOptions, useSegmentOptions } from './hooks/useStaticOptions';
import { useManagerProfile, useCompanyProfile } from './hooks/useProfileData';
import { useFieldEditing } from './hooks/useFieldEditing';
import { useCEP } from './hooks/useCEP';
import { useFieldValidation } from './hooks/useFieldSaving';
import { useSocialMedia } from './hooks/useSocialMedia';
import { EditableField } from './components/EditableField';
import { AddressField } from './components/AddressField';
import { SocialMediaField } from './components/SocialMediaField';
import { TextAreaField } from './components/TextAreaField';
import { EmailChangeField } from './components/EmailChangeField';
import { CompanyProfile, ResponseData, AddressData } from './types';
import { READONLY_FIELDS } from './constants';
import moment from 'moment';

const JobManagerProfile: React.FC = () => {
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responseData, setResponseData] = useState<ResponseData | null>(null);
  const hasLoadedRef = useRef(false);
  
  // Hooks para opções estáticas
  const genderOptions = useGenderOptions();
  const segmentOptions = useSegmentOptions();
  
  // Hooks para perfis
  const { managerProfile, loading: managerLoading, loadManagerProfile, updateManager } = useManagerProfile();
  const { companyProfile, loading: companyLoading, loadCompanyProfile, updateCompany } = useCompanyProfile();
  
  // Hook para edição de campos
  const { editingField, editingValue, setEditingValue, startEditing, cancelEditing } = useFieldEditing(segmentOptions, genderOptions);
  
  // Hook para validação
  const { validateField, formatValueForAPI: formatValue } = useFieldValidation();
  
  // Hook para redes sociais
  const {
    editingSocialMedia,
    showingSocialMedia,
    startEditing: startEditingSocialMedia,
    addSocialMedia,
    updateSocialMediaType,
    updateSocialMediaUrl,
    removeSocialMedia,
    toggleDisplay: toggleSocialMediaDisplay,
    resetEditing: resetSocialMediaEditing
  } = useSocialMedia();
  
  // Estado local para companyProfile (para atualizações de CEP)
  const [companyProfileLocal, setCompanyProfileLocal] = useState<CompanyProfile>(companyProfile);
  // Erro da API de CEP para exibir na tela
  const [cepError, setCepError] = useState<string | null>(null);
  
  // Sincronizar companyProfileLocal quando companyProfile muda (ex.: após updateCompany)
  useEffect(() => {
    if (companyProfile && Object.keys(companyProfile).length > 0) {
      setCompanyProfileLocal(companyProfile);
    }
  }, [companyProfile]);

  // Função para mostrar modal de resposta (definida antes do useCEP para uso no onCepError)
  const showResponseModal = useCallback((title: string, message: string, type: 'success' | 'error' = 'success'): void => {
    setResponseData({
      title,
      text: () => <p>{message}</p>,
      type
    });
  }, []);

  const onCompanyProfileUpdateFromCEP = useCallback((updates: Partial<CompanyProfile>) => {
    setCompanyProfileLocal(prev => ({ ...prev, ...updates }));
  }, []);

  const onCepError = useCallback(
    (message: string) => {
      setCepError(message);
      showResponseModal('CEP inválido', '', 'error');
    },
    [showResponseModal]
  );

  const onCepSuccess = useCallback(() => {
    setCepError(null);
  }, []);

  // Hook para CEP (com callback de erro para exibir na tela)
  const { handleCEPChange, loadingCEP } = useCEP(
    companyProfileLocal,
    onCompanyProfileUpdateFromCEP,
    onCepError,
    onCepSuccess
  );

  // Verificar bloqueio de acesso
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const parsedJWT = parseJwt(token);
      if (parsedJWT?.profiles?.includes('em_job_manager')) {
        navigate('/offers/panel');
        return;
      }
    }
  }, [navigate]);

  // Carregar dados iniciais
  useEffect(() => {
    // Evitar múltiplas execuções
    if (hasLoadedRef.current) {
      return;
    }
    
    hasLoadedRef.current = true;
    let isMounted = true;
    
    const loadData = async () => {
      try {
        setInitialLoading(true);
        await Promise.all([
          loadManagerProfile(),
          loadCompanyProfile()
        ]);
      } catch (error: unknown) {
        if (isMounted) {
          console.error('Erro ao carregar dados do perfil:', error);
          showResponseModal(
            'Erro',
            extractApiErrorMessage(error, 'Erro ao carregar dados do perfil.'),
            'error'
          );
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez no mount

  // Verificar se gênero é feminino
  const isFeminineGender = useCallback((gender: string): boolean => {
    if (!gender) return false;
    const label = genderOptions[gender]?.toLowerCase() || '';
    return label.includes('feminino') || label.includes('female');
  }, [genderOptions]);

  // Preparar opções de gênero para select
  const genderOptionsArray = useMemo(() => {
    return Object.keys(genderOptions).map(key => ({
      value: key,
      label: genderOptions[key]
    }));
  }, [genderOptions]);

  // Função para iniciar edição com tratamento especial
  const handleStartEditing = useCallback((field: string, currentValue: any): void => {
    if (READONLY_FIELDS.includes(field)) {
      return;
    }
    
    let value: any = currentValue;
    
    // Aplicar máscaras para campos que precisam de formatação visual
    if (field === 'cpf' && currentValue) {
      value = formatCPF(String(currentValue));
    } else if (field === 'company_cnpj' && currentValue) {
      value = formatCNPJ(String(currentValue));
    } else if (field === 'date_of_birth' && currentValue) {
      try {
        value = moment(currentValue).toDate();
      } catch (error) {
        console.error('Erro ao converter data:', error);
        value = currentValue;
      }
    }
    
    startEditing(field, value);
  }, [startEditing]);

  // Função para salvar campo
  const handleSaveField = useCallback(async (field: string, value: any): Promise<void> => {
    try {
      setSaving(true);
      
      // Validação
      const validationError = validateField(field, value);
      if (validationError) {
        setSaving(false);
        showResponseModal('Erro', validationError, 'error');
        return;
      }
      
      // Formatar valor
      let formattedValue = value;
      if (field === 'date_of_birth' && value) {
        formattedValue = formatDateForAPI(value);
      } else {
        formattedValue = formatValue(field, value);
      }
      
      if (field.startsWith('company_')) {
        // Atualizar campo da empresa
        const companyId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID);
        if (!companyId) {
          throw new Error('Company ID não encontrado');
        }
        
        const companyField = field.replace('company_', '');
        const updateData: any = {};
        
        // Mapear campos para o formato da API
        if (companyField === 'legal_name' || companyField === 'fantasy_name') {
          updateData.name = formattedValue;
        } else if (companyField === 'email') {
          updateData.email = formattedValue;
        } else if (companyField === 'phone') {
          updateData.phone = formattedValue;
        } else if (companyField === 'website') {
          updateData.website = formattedValue;
        } else if (companyField === 'segment') {
          updateData.segment_id = parseInt(String(formattedValue));
        } else {
          updateData[companyField] = formattedValue;
        }
        
        await updateCompany(updateData);
        cancelEditing();
        showResponseModal('Sucesso', 'Campo atualizado com sucesso!', 'success');
      } else {
        // Atualizar campo do manager
        await updateManager(field, formattedValue);
        cancelEditing();
        showResponseModal('Sucesso', 'Campo atualizado com sucesso!', 'success');
      }
    } catch (error: unknown) {
      console.error('Erro ao atualizar campo:', error);
      const errorMessage = extractApiErrorMessage(error, 'Erro ao atualizar campo. Tente novamente.');
      showResponseModal('Erro', errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  }, [validateField, formatValue, updateCompany, updateManager, cancelEditing, showResponseModal]);

  // Função para salvar endereço
  const handleSaveAddress = useCallback(async (addressData: AddressData): Promise<void> => {
    try {
      setSaving(true);
      
      const companyId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID);
      if (!companyId) {
        throw new Error('Company ID não encontrado');
      }
      
      const postcodeValue = addressData.postcode || '';
      const formattedPostcode = removeFormatting(String(postcodeValue));
      
      // Validação
      const validationError = validateField('company_address', addressData);
      if (validationError) {
        setSaving(false);
        showResponseModal('Erro', validationError, 'error');
        return;
      }
      if (cepError || loadingCEP) {
        setSaving(false);
        showResponseModal('Erro', cepError ? 'CEP inválido. Corrija o CEP antes de salvar.' : 'Aguarde a consulta do CEP terminar para salvar.', 'error');
        return;
      }
      
      // Montar endereço completo apenas com dados atuais do CEP (evita concatenar com endereço antigo)
      const addressFromCep = (companyProfileLocal.address || '').trim();
      const fullAddress =
        addressFromCep.includes(',')
          ? addressFromCep
          : [companyProfileLocal.address, companyProfileLocal.address_district, companyProfileLocal.city_name, companyProfileLocal.uf]
              .filter(Boolean)
              .join(', ');
      
      const updateData: Record<string, string | number> = {
        postcode: formattedPostcode,
        address_number: addressData.address_number || '',
        address_complement: addressData.address_complement || ''
      };
      if (fullAddress) {
        updateData.address = fullAddress;
      }
      
      // Incluir dados do CEP se disponíveis
      if (companyProfileLocal.address_district) {
        updateData.address_district = companyProfileLocal.address_district;
      }
      if (companyProfileLocal.city_id) {
        updateData.city_id = companyProfileLocal.city_id;
      }
      
      await updateCompany(updateData);
      cancelEditing();
      showResponseModal('Sucesso', 'Endereço atualizado com sucesso!', 'success');
    } catch (error: unknown) {
      console.error('Erro ao atualizar endereço:', error);
      const errorMessage = extractApiErrorMessage(error, 'Erro ao atualizar endereço. Tente novamente.');
      showResponseModal('Erro', errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  }, [companyProfileLocal, updateCompany, cancelEditing, validateField, showResponseModal, cepError, loadingCEP]);

  // Função para salvar redes sociais
  const handleSaveSocialMedia = useCallback(async (): Promise<void> => {
    try {
      setSaving(true);
      
      const companyId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID);
      if (!companyId) {
        throw new Error('Company ID não encontrado');
      }
      
      const socialMediaArray = editingSocialMedia
        .map((item) => ({
          type: item.type,
          url: String(item.url || '').trim()
        }))
        .filter((item) => item.url.length > 0);
      
      const updateData = convertSocialMediaToAPI(socialMediaArray);
      await updateCompany(updateData);
      resetSocialMediaEditing();
      showResponseModal('Sucesso', 'Redes sociais atualizadas com sucesso!', 'success');
    } catch (error: unknown) {
      console.error('Erro ao atualizar redes sociais:', error);
      const errorMessage = extractApiErrorMessage(error, 'Erro ao atualizar redes sociais. Tente novamente.');
      showResponseModal('Erro', errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  }, [editingSocialMedia, updateCompany, resetSocialMediaEditing, showResponseModal]);

  // Função para iniciar edição de endereço
  const handleStartEditingAddress = useCallback((profile: CompanyProfile): void => {
    setCepError(null);
    startEditing('company_address', {
      postcode: profile?.postcode || '',
      address_number: profile?.address_number || '',
      address_complement: profile?.address_complement || ''
    });
  }, [startEditing]);

  // Cancelar edição de endereço: reverter estado local ao perfil do servidor
  const handleCancelAddress = useCallback((): void => {
    setCepError(null);
    setCompanyProfileLocal(companyProfile);
    cancelEditing();
  }, [companyProfile, cancelEditing]);

  // Função para iniciar edição de redes sociais
  const handleStartEditingSocialMedia = useCallback((): void => {
    const currentSocialMedia = companyProfile.social_media || [];
    startEditingSocialMedia(currentSocialMedia);
    // O editingSocialMedia será atualizado pelo hook, então usamos ele
    startEditing('company_social_media', currentSocialMedia);
  }, [companyProfile.social_media, startEditingSocialMedia, startEditing]);
  
  // Sincronizar editingValue quando editingSocialMedia muda durante edição
  useEffect(() => {
    if (editingField === 'company_social_media') {
      setEditingValue(editingSocialMedia);
    }
  }, [editingSocialMedia, editingField, setEditingValue]);

  // Função para atualizar endereço durante edição
  const handleAddressChange = useCallback((addressData: AddressData): void => {
    setEditingValue(addressData);
  }, [setEditingValue]);

  // Chamar busca de CEP (erro só é limpo quando a consulta for bem-sucedida)
  const handleCEPChangeWithClear = useCallback((cepValue: string): void => {
    handleCEPChange(cepValue);
  }, [handleCEPChange]);

  // Função para atualizar email após alteração bem-sucedida
  const handleEmailChanged = useCallback(async (_newEmail?: string): Promise<void> => {
    try {
      await loadManagerProfile();
      showResponseModal('Sucesso', 'E-mail alterado com sucesso!', 'success');
    } catch (error: unknown) {
      console.error('Erro ao atualizar perfil após alteração de email:', error);
      showResponseModal(
        'E-mail alterado',
        'E-mail alterado com sucesso. Se não atualizou na tela, atualize a página.',
        'success'
      );
    }
  }, [loadManagerProfile, showResponseModal]);

  // Preparar opções de segmento para select
  const getSegmentOptionsForField = useCallback(() => {
    if (editingField === 'company_segment' && segmentOptions.length > 0) {
      const currentSegmentId = companyProfile.segment_id;
      if (currentSegmentId !== undefined && currentSegmentId !== null) {
        const segmentIdStr = String(currentSegmentId);
        const foundOption = segmentOptions.find(opt => String(opt.value) === segmentIdStr);
        if (foundOption && !editingValue) {
          setEditingValue(foundOption.value);
        }
      }
    }
    return segmentOptions;
  }, [editingField, segmentOptions, companyProfile.segment_id, editingValue, setEditingValue]);

  // Preparar opções de gênero para select
  const getGenderOptionsForField = useCallback(() => {
    if (editingField === 'gender' && genderOptionsArray.length > 0) {
      const currentGender = managerProfile.gender;
      if (currentGender && !editingValue) {
        const foundOption = genderOptionsArray.find(opt => opt.value === currentGender);
        if (foundOption) {
          setEditingValue(foundOption.value);
        }
      }
    }
    return genderOptionsArray;
  }, [editingField, genderOptionsArray, managerProfile.gender, editingValue, setEditingValue]);

  if (initialLoading || managerLoading || companyLoading) {
    return (
      <div className='job-manager-profile company-page'>
        <div className='profile-container'>
          <div className='loading-container'>
            <p>Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentCompanyProfile = companyProfileLocal || companyProfile;

  return (
    <div className='job-manager-profile company-page'>
      <div className='profile-container'>
        {/* Card do Perfil Pessoal */}
        <div className='block personal-profile'>
          {editingField === 'full_name' ? (
            <div className='editing-title'>
              <Input
                name='full_name'
                value={editingValue}
                onChange={(val) => setEditingValue(val)}
              />
              <div className='edit-actions'>
                <button 
                  className='save-btn' 
                  onClick={() => handleSaveField('full_name', editingValue)}
                  disabled={saving}
                >
                  Salvar
                </button>
                <button 
                  className='cancel-btn' 
                  onClick={cancelEditing}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <h3>
              {managerProfile.full_name || 'Nome não informado'}
              <img 
                src={editIcon} 
                alt='edit' 
                className='edit-icon' 
                onClick={() => handleStartEditing('full_name', managerProfile.full_name)}
              />
            </h3>
          )}
          {currentCompanyProfile && (
            <p className='role-text'>
              Administrador{isFeminineGender(managerProfile.gender) ? 'a' : ''} da empresa: {currentCompanyProfile.legal_name || currentCompanyProfile.name || 'N/A'}
            </p>
          )}
          <div className='form-content'>
            <EditableField
              icon={documentIcon}
              label='CPF'
              value={formatCPF(managerProfile.cpf || '')}
              fieldName='cpf'
              type='text'
              mask='999.999.999-99'
              rawValue={managerProfile.cpf}
              isEditing={editingField === 'cpf'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EmailChangeField
              icon={emailIcon}
              label='E-mail'
              currentEmail={managerProfile.manager_email || ''}
              onEmailChanged={handleEmailChanged}
              showLabel={false}
            />
            <EditableField
              icon={birthdateIcon}
              label='Data de nascimento'
              value={formatDate(managerProfile.date_of_birth)}
              fieldName='date_of_birth'
              type='date'
              rawValue={managerProfile.date_of_birth}
              showLabel={false}
              isEditing={editingField === 'date_of_birth'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EditableField
              icon={maleIcon}
              label='Gênero'
              value={genderOptions[managerProfile.gender] || managerProfile.gender || ''}
              fieldName='gender'
              type='select'
              options={getGenderOptionsForField()}
              rawValue={managerProfile.gender}
              showLabel={true}
              allowEmpty={false}
              isEditing={editingField === 'gender'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            
            <div className='password-section'>
              <h4>Senha</h4>
              <button 
                type='button'
                className='change-password-link' 
                onClick={() => {
                  navigate('/reset-password', { state: { fromProfile: true } });
                }}
              >
                Alterar senha
              </button>
            </div>
          </div>
        </div>

        {/* Card do Perfil da Empresa */}
        <div className='block company-profile'>
          <h3>
            {currentCompanyProfile?.logo && (
              <img src={currentCompanyProfile.logo} alt='company logo' className='company-logo' />
            )}
            {currentCompanyProfile?.legal_name || currentCompanyProfile?.name || 'Nome da Empresa'}
          </h3>
          <div className='form-content'>
            <EditableField
              icon={documentIcon}
              label='CNPJ'
              value={formatCNPJ(currentCompanyProfile?.cnpj || '')}
              fieldName='company_cnpj'
              type='text'
              mask='99.999.999/9999-99'
              rawValue={currentCompanyProfile?.cnpj}
              isEditing={editingField === 'company_cnpj'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EditableField
              icon={badgeIcon}
              label='Nome fantasia'
              value={currentCompanyProfile?.fantasy_name || ''}
              fieldName='company_fantasy_name'
              type='text'
              rawValue={currentCompanyProfile?.fantasy_name}
              isEditing={editingField === 'company_fantasy_name'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <AddressField
              icon={locationIcon}
              label='Endereço'
              companyProfile={currentCompanyProfile}
              isEditing={editingField === 'company_address'}
              editingAddress={(editingValue as AddressData) || {}}
              loading={saving}
              cepError={cepError}
              cepLoading={loadingCEP}
              onStartEdit={handleStartEditingAddress}
              onSave={handleSaveAddress}
              onCancel={handleCancelAddress}
              onAddressChange={handleAddressChange}
              onCEPChange={handleCEPChangeWithClear}
            />
            <EditableField
              icon={companyIcon}
              label='Segmento'
              value={currentCompanyProfile?.segment_name || currentCompanyProfile?.segment || ''}
              fieldName='company_segment'
              type='select'
              options={getSegmentOptionsForField()}
              rawValue={currentCompanyProfile?.segment_id || currentCompanyProfile?.segment_name || currentCompanyProfile?.segment}
              showLabel={true}
              isSegment={true}
              isEditing={editingField === 'company_segment'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EditableField
              icon={emailIcon}
              label='E-mail da empresa'
              value={currentCompanyProfile?.company_email || ''}
              fieldName='company_email'
              type='email'
              rawValue={currentCompanyProfile?.company_email}
              isEditing={editingField === 'company_email'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EditableField
              icon={phoneIcon}
              label='Telefone'
              value={formatPhone(currentCompanyProfile?.phone || '')}
              fieldName='company_phone'
              type='text'
              mask='(99) 99999-9999'
              rawValue={currentCompanyProfile?.phone}
              isEditing={editingField === 'company_phone'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <EditableField
              icon={linkIcon}
              label='Site'
              value={currentCompanyProfile?.website || ''}
              fieldName='company_website'
              type='text'
              maxLength={200}
              rawValue={currentCompanyProfile?.website}
              isEditing={editingField === 'company_website'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <SocialMediaField
              icon={linkIcon}
              label='Rede social'
              companyProfile={currentCompanyProfile}
              isEditing={editingField === 'company_social_media'}
              editingSocialMedia={editingField === 'company_social_media' ? editingSocialMedia : []}
              loading={saving}
              showingSocialMedia={showingSocialMedia}
              onStartEdit={handleStartEditingSocialMedia}
              onSave={handleSaveSocialMedia}
              onCancel={() => {
                cancelEditing();
                resetSocialMediaEditing();
              }}
              onToggleDisplay={toggleSocialMediaDisplay}
              onAddSocialMedia={addSocialMedia}
              onUpdateType={updateSocialMediaType}
              onUpdateUrl={updateSocialMediaUrl}
              onRemoveSocialMedia={removeSocialMedia}
            />
            
            <TextAreaField
              label='Sobre a empresa'
              value={currentCompanyProfile?.description ?? ''}
              fieldName='company_description'
              showColon={true}
              isEditing={editingField === 'company_description'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
            <TextAreaField
              label='Por que trabalhar com a gente?'
              value={currentCompanyProfile?.motivation ?? ''}
              fieldName='company_motivation'
              showColon={false}
              isEditing={editingField === 'company_motivation'}
              editingValue={editingValue}
              loading={saving}
              onStartEdit={handleStartEditing}
              onSave={handleSaveField}
              onCancel={cancelEditing}
              onValueChange={setEditingValue}
            />
          </div>
        </div>
      </div>
      
      <ResponseModal
        render={!!responseData}
        type={responseData?.type || 'success'}
        title={responseData?.title || ''}
        text={responseData?.text}
        onClose={() => setResponseData(null)}
      />
    </div>
  );
};

export default JobManagerProfile;
